/**
 * usePaymentNotification Hook
 * 
 * Handles payment success/failure notifications after Stripe redirect.
 * Uses localStorage to persist notification state across page reloads,
 * ensuring the toast message is shown reliably.
 */

import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

const PAYMENT_NOTIFICATION_KEY = 'payment_notification_pending';

/**
 * Save a pending payment notification to localStorage
 * Called before the page reload or during initial detection
 */
export const savePendingPaymentNotification = (type, sessionId, purchaseType) => {
  const notification = {
    type,
    sessionId,
    purchaseType,
    timestamp: Date.now()
  };
  localStorage.setItem(PAYMENT_NOTIFICATION_KEY, JSON.stringify(notification));
};

/**
 * Get and clear pending payment notification from localStorage
 */
export const getPendingPaymentNotification = () => {
  try {
    const stored = localStorage.getItem(PAYMENT_NOTIFICATION_KEY);
    if (stored) {
      localStorage.removeItem(PAYMENT_NOTIFICATION_KEY);
      const notification = JSON.parse(stored);
      // Only return if notification is less than 5 minutes old
      if (Date.now() - notification.timestamp < 5 * 60 * 1000) {
        return notification;
      }
    }
  } catch (e) {
    console.error('Error reading payment notification:', e);
    localStorage.removeItem(PAYMENT_NOTIFICATION_KEY);
  }
  return null;
};

/**
 * Custom hook to handle payment notifications after Stripe redirect
 * 
 * @param {Object} options
 * @param {Function} options.onPaymentSuccess - Callback when payment is verified
 * @param {Function} options.onPaymentCancelled - Callback when payment is cancelled
 * @param {string} options.expectedPurchaseType - Expected purchase type (e.g., 'analysis_pack', 'seat')
 * @param {boolean} options.shouldProcess - Whether to process the payment (e.g., wait for org to load)
 */
export const usePaymentNotification = ({
  onPaymentSuccess,
  onPaymentCancelled,
  expectedPurchaseType = null,
  shouldProcess = true
} = {}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const hasProcessedRef = useRef(false);

  useEffect(() => {
    // Check for pending notification from localStorage first (after page reload)
    const pending = getPendingPaymentNotification();
    if (pending && pending.type === 'success' && !hasProcessedRef.current) {
      hasProcessedRef.current = true;
      // Show the toast after a brief delay to ensure DOM is ready
      requestAnimationFrame(() => {
        showSuccessToast(pending.purchaseType);
        if (onPaymentSuccess) {
          onPaymentSuccess(pending.sessionId, pending.purchaseType);
        }
      });
      return;
    }

    // Check URL params for payment callback
    const paymentStatus = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');
    const purchaseType = searchParams.get('type');

    // Skip if no payment params or already processed
    if (!paymentStatus || hasProcessedRef.current) return;

    // Skip if waiting for dependencies (e.g., organization to load)
    if (!shouldProcess) return;

    // Optionally filter by purchase type
    if (expectedPurchaseType && purchaseType !== expectedPurchaseType) return;

    hasProcessedRef.current = true;

    if (paymentStatus === 'success' && sessionId) {
      // Save to localStorage immediately in case component remounts
      savePendingPaymentNotification('success', sessionId, purchaseType);
      
      // Clear URL params
      setSearchParams({});
      
      // Show toast and trigger callback
      requestAnimationFrame(() => {
        // Re-read from localStorage to ensure we have the latest
        const savedNotification = getPendingPaymentNotification();
        if (savedNotification) {
          showSuccessToast(savedNotification.purchaseType);
          if (onPaymentSuccess) {
            onPaymentSuccess(savedNotification.sessionId, savedNotification.purchaseType);
          }
        }
      });
    } else if (paymentStatus === 'cancelled') {
      setSearchParams({});
      toast.info('Payment was cancelled. No charges were made.');
      if (onPaymentCancelled) {
        onPaymentCancelled();
      }
    }
  }, [searchParams, shouldProcess, expectedPurchaseType, onPaymentSuccess, onPaymentCancelled, setSearchParams]);
};

/**
 * Show appropriate success toast based on purchase type
 */
const showSuccessToast = (purchaseType) => {
  switch (purchaseType) {
    case 'analysis_pack':
      toast.success('Analysis pack purchased successfully! Your balance has been updated.');
      break;
    case 'seat':
      toast.success('Seat purchase successful! Your team seats have been added.');
      break;
    case 'agent':
      toast.success('Agent subscription activated! Your agent is now ready.');
      break;
    case 'subscription':
    default:
      toast.success('Subscription activated successfully! Welcome to your new plan.');
      break;
  }
};

/**
 * Utility to manually show a payment success toast
 * Use when you need to show the toast after custom verification
 */
export const showPaymentSuccessToast = (purchaseType, customMessage = null) => {
  if (customMessage) {
    toast.success(customMessage);
  } else {
    showSuccessToast(purchaseType);
  }
};

export default usePaymentNotification;
